import { PeriodType } from "@/utils/period";

// Utilidades compartilhadas para gerar dados fictícios plausíveis nas
// páginas de Relatórios da conta Preview.

export const randomInt = (min: number, max: number): number =>
  Math.floor(Math.random() * (max - min + 1)) + min;

export const randomFloat = (min: number, max: number, decimals = 2): number =>
  Number((Math.random() * (max - min) + min).toFixed(decimals));

export const shuffle = <T>(items: T[]): T[] => {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
};

export const pointCountForPeriod = (
  period: PeriodType,
  startDate?: Date,
  endDate?: Date,
): number => {
  switch (period) {
    case "today":
    case "yesterday":
      return 24;
    case "last7":
      return 7;
    case "currentMonth":
    case "lastMonth":
      return 30;
    case "last30":
      return 30;
    case "last60":
      return 60;
    case "custom": {
      if (startDate && endDate) {
        const diffDays = Math.max(
          1,
          Math.round(
            (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24),
          ) + 1,
        );
        return Math.min(diffDays, 90);
      }
      return 7;
    }
    default:
      return 7;
  }
};

// Gera uma série de valores para o período todo. Combina duas ondas de
// frequências diferentes (em vez de uma senoide única), uma leve tendência
// de alta ou baixa, e picos ocasionais (tipo "dia de campanha"/post viral)
// para o gráfico não parecer sempre a mesma curva repetida — e sempre com
// piso mínimo, para não ficar vazio no início e só aparecer no fim.
export const buildWaveSeries = (
  period: PeriodType,
  average: number,
  options?: { floorRatio?: number; minAbsolute?: number; integer?: boolean },
): number[] => {
  const count = pointCountForPeriod(period);
  const floorRatio = options?.floorRatio ?? 0.55;
  const minAbsolute = options?.minAbsolute ?? 1;
  const integer = options?.integer ?? true;

  // average = floor + range/2, onde floor = peak*floorRatio e range = peak*(1-floorRatio)
  const meanFactor = floorRatio + (1 - floorRatio) / 2;
  let peak = Math.max(minAbsolute / floorRatio, average / meanFactor);
  let floor = Math.max(minAbsolute, peak * floorRatio);
  if (floor >= peak) {
    peak = floor + Math.max(minAbsolute * 0.6, 1);
  }
  const range = peak - floor;

  // Onda principal (formato geral da curva) + onda secundária mais rápida
  // (textura), somadas para um resultado menos "perfeitamente senoidal".
  const primaryCycles = randomFloat(1.1, 2.6);
  const primaryPhase = randomFloat(0, Math.PI * 2);
  const secondaryCycles = randomFloat(3, 6.5);
  const secondaryPhase = randomFloat(0, Math.PI * 2);
  const secondaryWeight = randomFloat(0.15, 0.35);

  // Tendência leve de alta ou baixa ao longo do período (metade das séries
  // sobe, metade cai), para variar entre gráficos e recarregamentos.
  const driftDirection = Math.random() < 0.5 ? -1 : 1;
  const driftStrength = randomFloat(0.15, 0.45) * driftDirection;

  // 0-2 pontos de destaque (ex.: campanha, post viral) para dar um "furo"
  // visual na curva, quando o período tem pontos suficientes.
  const spikeCount = count > 4 ? randomInt(0, 2) : 0;
  const spikeIndexes = new Set<number>();
  while (spikeIndexes.size < spikeCount) {
    spikeIndexes.add(randomInt(1, count - 2));
  }

  return Array.from({ length: count }, (_, index) => {
    const t = count > 1 ? index / (count - 1) : 0;
    const primaryWave = Math.sin(t * Math.PI * primaryCycles + primaryPhase);
    const secondaryWave = Math.sin(t * Math.PI * secondaryCycles + secondaryPhase);
    const combinedWave =
      primaryWave * (1 - secondaryWeight) + secondaryWave * secondaryWeight;
    const drift = driftStrength * (t - 0.5);
    const value = floor + range * (0.5 + 0.5 * combinedWave) + range * drift;
    const noise = randomFloat(-range * 0.22, range * 0.22);

    let result = value + noise;
    if (spikeIndexes.has(index)) {
      result += range * randomFloat(0.6, 1.2);
    }

    result = Math.max(minAbsolute, result);
    return integer ? Math.round(result) : Number(result.toFixed(2));
  });
};

// Divide um total entre `count` partes, com pesos aleatórios (não uniforme,
// para parecer orgânico), mas cuja soma bate com o total — usado para que
// detalhamentos (por fonte, por página, top itens) façam sentido com os
// números principais em vez de serem sorteados de forma totalmente
// independente.
export const distributeTotal = (
  total: number,
  count: number,
  options?: { minEach?: number },
): number[] => {
  if (count <= 0) return [];
  const minEach = Math.max(0, options?.minEach ?? 0);
  const safeTotal = Math.max(total, minEach * count);
  const weights = Array.from({ length: count }, () => randomFloat(0.5, 1.8));
  const weightSum = weights.reduce((a, b) => a + b, 0);
  return weights.map((w) => Math.max(minEach, Math.round((w / weightSum) * safeTotal)));
};

// Mesma distribuição acima, mas já no formato Record<nome, valor> usado
// pelos blocos de Tags/List.
export const buildWeightedRecord = (
  names: string[],
  total: number,
  options?: { minEach?: number },
): Record<string, number> => {
  const values = distributeTotal(total, names.length, options);
  return objectFromEntries(names.map((name, index) => [name, values[index]] as [string, number]));
};

export const pointsFromSeries = (
  period: PeriodType,
  series: number[],
): { period: string; points: Array<{ x: number; y: number }> } => ({
  period,
  points: series.map((y, x) => ({ x, y })),
});

export const scaleForPeriod = (period: PeriodType): number => {
  switch (period) {
    case "today":
    case "yesterday":
      return 0.15;
    case "last7":
      return 1;
    case "currentMonth":
    case "lastMonth":
      return 4;
    case "last30":
      return 4.2;
    case "last60":
      return 8;
    default:
      return 1;
  }
};

export const formatCurrencyBRL = (value: number): string =>
  `R$${value.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const objectFromEntries = (
  entries: Array<[string, number]>,
): Record<string, number> =>
  entries.reduce<Record<string, number>>((acc, [key, value]) => {
    acc[key] = value;
    return acc;
  }, {});

export const PRODUCT_NAMES = [
  "Camiseta Oversized Preta",
  "Tênis Runner Branco",
  "Jaqueta Corta-Vento",
  "Calça Cargo Bege",
  "Boné Trucker Logo",
  "Moletom Canguru Cinza",
];

// Capitais espalhadas pelas cinco regiões do Brasil, com coordenadas reais
// aproximadas, para o mapa de visitantes não ficar concentrado num só ponto.
export const BR_CITIES = [
  { name: "São Paulo", state: "SP", lat: -23.5505, lon: -46.6333 },
  { name: "Rio de Janeiro", state: "RJ", lat: -22.9068, lon: -43.1729 },
  { name: "Belo Horizonte", state: "MG", lat: -19.9167, lon: -43.9345 },
  { name: "Salvador", state: "BA", lat: -12.9777, lon: -38.5016 },
  { name: "Fortaleza", state: "CE", lat: -3.7172, lon: -38.5433 },
  { name: "Recife", state: "PE", lat: -8.0476, lon: -34.877 },
  { name: "Brasília", state: "DF", lat: -15.7939, lon: -47.8828 },
  { name: "Curitiba", state: "PR", lat: -25.4284, lon: -49.2733 },
  { name: "Porto Alegre", state: "RS", lat: -30.0346, lon: -51.2177 },
  { name: "Manaus", state: "AM", lat: -3.119, lon: -60.0217 },
  { name: "Belém", state: "PA", lat: -1.4558, lon: -48.4902 },
  { name: "Goiânia", state: "GO", lat: -16.6869, lon: -49.2648 },
];

export const CITY_NAMES = BR_CITIES.map((city) => city.name);

export const COUNTRY_NAMES = ["Brasil", "Portugal", "Estados Unidos"];

// Mesma lista de fontes de tráfego padrão do app real
// (ver DEFAULT_TRAFFIC_SOURCES em src/hooks/useTrafficSources.ts), para o
// mockup cobrir todas as opções fixas, não só um subconjunto.
export const TRAFFIC_SOURCES = [
  "site-blog",
  "tiktok",
  "google",
  "google-ads",
  "facebook",
  "meta-ads",
  "instagram",
  "whatsapp",
  "x",
  "email-marketing",
  "stories",
];

export const CAMPAIGN_NAMES = [
  "Campanha Institucional",
  "Promoção Coleção Nova",
  "Remarketing Carrinho",
  "Lançamento Verão",
];

export type { PeriodType };
